import React from "react";
import { useState, useEffect, useCallback } from "react";
import { api } from "../../api/client";
import { GENDER_BG_COLORS, timePattern, timeRegex } from "../../pages/AdminDashboard";
import { carColors } from "./TabTrips";
import { getCurrentDateInKarachi } from "../../utils/dateUtils";

const TripCreate = () => {
  const [cars, setCars] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [tripForm, setTripForm] = useState({
    tripType: "morning",
    minTime: "",
    maxTime: "",
    date: getCurrentDateInKarachi(),
  });

  const [selectedCars, setSelectedCars] = useState([]);
  const [eligiblePassengers, setEligiblePassengers] = useState([]);
  const [unassignedPassengers, setUnassignedPassengers] = useState([]);
  const [passengerCount, setPassengerCount] = useState(null);
  const [passengerCountLoading, setPassengerCountLoading] = useState(false);
  const [bulkSelectedPassengers, setBulkSelectedPassengers] = useState({});
  const [stops, setStops] = useState([]);

  const load = async () => {
    const carRes = await api("/admin/cars");
    setCars(carRes.cars);
    const userRes = await api("/admin/users/all");
    setAllUsers(userRes.users.filter(u => u.role === "passenger" && u.status === "approved"));
    const stopsRes = await api("/admin/stops");
    setStops(stopsRes.stops);
  };

  useEffect(() => {
    load();
    
    const stored = localStorage.getItem("tripCreateData");
    if (stored) {
      try {
        const data = JSON.parse(stored);
        setTripForm(data.tripForm || tripForm);
        setSelectedCars(data.selectedCars || []);
      } catch (e) {
        console.error("Failed to load stored trip data", e);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedCars.length > 0 || tripForm.date || tripForm.minTime || tripForm.maxTime) {
      const dataToStore = {
        tripForm,
        selectedCars,
      };
      localStorage.setItem("tripCreateData", JSON.stringify(dataToStore));
    }
  }, [tripForm, selectedCars]);

  const fetchPassengerCount = useCallback(async () => {
    if (!tripForm.date || !tripForm.minTime || !tripForm.maxTime || !tripForm.tripType) {
      setPassengerCount(null);
      setEligiblePassengers([]);
      setUnassignedPassengers([]);
      return;
    }

    if (!timeRegex.test(tripForm.minTime) || !timeRegex.test(tripForm.maxTime)) {
      setPassengerCount(null);
      setEligiblePassengers([]);
      setUnassignedPassengers([]);
      return;
    }

    setPassengerCountLoading(true);
    try {
      const response = await api(
        `/admin/trips/passenger-count?date=${tripForm.date}&minTime=${tripForm.minTime}&maxTime=${tripForm.maxTime}&tripType=${tripForm.tripType}`
      );
      setPassengerCount(response);
      
      if (response.passengers && Array.isArray(response.passengers)) {
        setEligiblePassengers(response.passengers);
        
        const assignedIds = new Set();
        selectedCars.forEach(car => {
          car.passengers.forEach(p => assignedIds.add(p._id));
        });
        
        setUnassignedPassengers(
          response.passengers.filter(p => !assignedIds.has(p._id))
        );
      }
    } catch (err) {
      console.error("Failed to fetch passenger count");
      setPassengerCount(null);
      setEligiblePassengers([]);
      setUnassignedPassengers([]);
    }
    setPassengerCountLoading(false);
  }, [tripForm.date, tripForm.minTime, tripForm.maxTime, tripForm.tripType, selectedCars]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPassengerCount();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [fetchPassengerCount]);

  const toggleCarSelection = (carId) => {
    const car = cars.find(c => c._id === carId);
    if (!car) return;

    const isSelected = selectedCars.some(c => c.carId === carId);
    
    if (isSelected) {
      const removedCar = selectedCars.find(c => c.carId === carId);
      setSelectedCars(selectedCars.filter(c => c.carId !== carId));
      
      if (removedCar) {
        setUnassignedPassengers([...unassignedPassengers, ...removedCar.passengers]);
      }
    } else {
      const passengersToAssign = unassignedPassengers.slice(0, car.capacity);
      const newCar = {
        carId: car._id,
        car: car,
        passengers: passengersToAssign,
        note: "",
        coordinators: [],
      };
      setSelectedCars([...selectedCars, newCar]);
      setUnassignedPassengers(unassignedPassengers.filter(p => !passengersToAssign.includes(p)));
    }
  };

  const updateCarNote = (carId, note) => {
    setSelectedCars(selectedCars.map(c => 
      c.carId === carId ? { ...c, note } : c
    ));
  };

  const removePassengerFromCar = (carId, passengerId) => {
    const car = selectedCars.find(c => c.carId === carId);
    if (!car) return;
    
    const passenger = car.passengers.find(p => p._id === passengerId);
    if (!passenger) return;
    
    setSelectedCars(selectedCars.map(c => {
      if (c.carId === carId) {
        return {
          ...c,
          passengers: c.passengers.filter(p => p._id !== passengerId),
          coordinators: c.coordinators.filter(cid => cid !== passengerId),
        };
      }
      return c;
    }));
    
    setUnassignedPassengers([...unassignedPassengers, passenger]);
  };

  const addPassengerToCar = (carId, passenger) => {
    setSelectedCars(selectedCars.map(c => {
      if (c.carId === carId) {
        return {
          ...c,
          passengers: [...c.passengers, passenger],
        };
      }
      return c;
    }));
    
    setUnassignedPassengers(unassignedPassengers.filter(p => p._id !== passenger._id));
  };

  const movePassenger = (fromCarId, toCarId, passengerId) => {
    const fromCar = selectedCars.find(c => c.carId === fromCarId);
    if (!fromCar) return;
    
    const passenger = fromCar.passengers.find(p => p._id === passengerId);
    if (!passenger) return;
    
    setSelectedCars(selectedCars.map(c => {
      if (c.carId === fromCarId) {
        return {
          ...c,
          passengers: c.passengers.filter(p => p._id !== passengerId),
          coordinators: c.coordinators.filter(cid => cid !== passengerId),
        };
      }
      if (c.carId === toCarId) {
        return {
          ...c,
          passengers: [...c.passengers, passenger],
        };
      }
      return c;
    }));
  };

  const toggleCoordinator = (carId, passengerId) => {
    setSelectedCars(selectedCars.map(c => {
      if (c.carId === carId) {
        const isCoordinator = c.coordinators.includes(passengerId);
        return {
          ...c,
          coordinators: isCoordinator
            ? c.coordinators.filter(id => id !== passengerId)
            : [...c.coordinators, passengerId],
        };
      }
      return c;
    }));
  };

  const bulkRemovePassengers = (carId, passengerIds) => {
    const car = selectedCars.find(c => c.carId === carId);
    if (!car) return;
    
    const passengersToRemove = car.passengers.filter(p => passengerIds.includes(p._id));
    
    setSelectedCars(selectedCars.map(c => {
      if (c.carId === carId) {
        return {
          ...c,
          passengers: c.passengers.filter(p => !passengerIds.includes(p._id)),
          coordinators: c.coordinators.filter(cid => !passengerIds.includes(cid)),
        };
      }
      return c;
    }));
    
    setUnassignedPassengers([...unassignedPassengers, ...passengersToRemove]);
    
    // Clear selections for this car
    setBulkSelectedPassengers(prev => {
      const newState = { ...prev };
      delete newState[carId];
      return newState;
    });
  };

  const bulkMovePassengers = (fromCarId, toCarId, passengerIds) => {
    const fromCar = selectedCars.find(c => c.carId === fromCarId);
    if (!fromCar) return;
    
    const passengersToMove = fromCar.passengers.filter(p => passengerIds.includes(p._id));
    
    setSelectedCars(selectedCars.map(c => {
      if (c.carId === fromCarId) {
        return {
          ...c,
          passengers: c.passengers.filter(p => !passengerIds.includes(p._id)),
          coordinators: c.coordinators.filter(cid => !passengerIds.includes(cid)),
        };
      }
      if (c.carId === toCarId) {
        return {
          ...c,
          passengers: [...c.passengers, ...passengersToMove],
        };
      }
      return c;
    }));
    
    // Clear selections for this car
    setBulkSelectedPassengers(prev => {
      const newState = { ...prev };
      delete newState[fromCarId];
      return newState;
    });
  };

  const togglePassengerSelection = (carId, passengerId) => {
    setBulkSelectedPassengers(prev => {
      const carSelections = prev[carId] || [];
      const isSelected = carSelections.includes(passengerId);
      
      if (isSelected) {
        const newSelections = carSelections.filter(id => id !== passengerId);
        if (newSelections.length === 0) {
          const newState = { ...prev };
          delete newState[carId];
          return newState;
        }
        return { ...prev, [carId]: newSelections };
      } else {
        return { ...prev, [carId]: [...carSelections, passengerId] };
      }
    });
  };

  const toggleSelectAllInCar = (carId, allPassengerIds) => {
    setBulkSelectedPassengers(prev => {
      const carSelections = prev[carId] || [];
      const allSelected = allPassengerIds.every(id => carSelections.includes(id));
      
      if (allSelected) {
        const newState = { ...prev };
        delete newState[carId];
        return newState;
      } else {
        return { ...prev, [carId]: allPassengerIds };
      }
    });
  };

  const createTrip = async (e) => {
    e.preventDefault();

    if (selectedCars.length === 0) {
      alert("Please select at least one vehicle");
      return;
    }

    if (!timeRegex.test(tripForm.minTime)) {
      alert("Invalid min-time format. Use HH:mm");
      return;
    }
    if (!timeRegex.test(tripForm.maxTime)) {
      alert("Invalid max-time format. Use HH:mm");
      return;
    }

    if (unassignedPassengers.length > 0) {
      const confirm = window.confirm("There are still unassigned passengers. Do you want to continue?");
      if (!confirm) return;
    }

    const [minh, minm] = tripForm.minTime.split(":").map(Number);
    const [maxh, maxm] = tripForm.maxTime.split(":").map(Number);
    const minMins = minh * 60 + minm;
    const maxMins = maxh * 60 + maxm;

    if (maxMins <= minMins) {
      alert("max-time must be later than min-time");
      return;
    }

    try {
      setIsSubmitting(true);

      const getStopOrder = (passenger) => {
        const stopId = passenger?.stop?._id || passenger?.stop;
        const stop = stops.find((s) => s._id === stopId);
        return stop ? stop.order : 999;
      };
      
      const carsWithPassengers = selectedCars.map(c => ({
        carId: c.carId,
        passengers: c.passengers
          .slice()
          .sort((a, b) => {
            const orderA = getStopOrder(a);
            const orderB = getStopOrder(b);
            return tripForm.tripType === "morning" ? orderA - orderB : orderB - orderA;
          })
          .map((p) => p._id),
        note: c.note,
        coordinators: c.coordinators,
      }));
      
      const r = await api("/admin/trips/create", {
        method: "POST",
        body: { 
          ...tripForm, 
          carsWithPassengers,
        },
      });

      alert("Trip saved as draft successfully");

      setTripForm({
        tripType: "morning",
        minTime: "",
        maxTime: "",
        date: "",
      });
      setSelectedCars([]);
      setEligiblePassengers([]);
      setUnassignedPassengers([]);
      setPassengerCount(null);
      
      localStorage.removeItem("tripCreateData");
    } catch (err) {
      alert(err.message || "Failed to create trip");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h4>Create New Trip</h4>
      <form onSubmit={createTrip} style={{ maxWidth: 1200 }}>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(2, 1fr)", 
          gap: "1rem",
          marginBottom: "1rem"
        }}>
          <div>
            <label>Date</label>
            <input
              type="date"
              value={tripForm.date}
              onChange={(e) => setTripForm((f) => ({ ...f, date: e.target.value }))}
              required
            />
          </div>

          <div>
            <label>Trip Type</label>
            <select
              value={tripForm.tripType}
              onChange={(e) => setTripForm((f) => ({ ...f, tripType: e.target.value }))}
              required
            >
              <option value="morning">Morning</option>
              <option value="evening">Evening</option>
            </select>
          </div>

          <div>
            <label>Min Time - Time Range Start (HH:mm)</label>
            <input
              type="text"
              placeholder="HH:mm (24-hr)"
              pattern={timePattern}
              value={tripForm.minTime}
              onChange={(e) => setTripForm((f) => ({ ...f, minTime: e.target.value }))}
              required
            />
            {tripForm.minTime && !timeRegex.test(tripForm.minTime) && (
              <span style={{ color: "red", fontSize: "0.8em" }}>
                Invalid time (24-hr)
              </span>
            )}
          </div>

          <div>
            <label>Max Time - Time Range End (HH:mm)</label>
            <input
              type="text"
              placeholder="HH:mm (24-hr)"
              pattern={timePattern}
              value={tripForm.maxTime}
              onChange={(e) => setTripForm((f) => ({ ...f, maxTime: e.target.value }))}
              required
            />
            {tripForm.maxTime && !timeRegex.test(tripForm.maxTime) && (
              <span style={{ color: "red", fontSize: "0.8em" }}>
                Invalid time (24-hr)
              </span>
            )}
          </div>
        </div>

        {tripForm.date && tripForm.minTime && tripForm.maxTime && tripForm.tripType && (
          <div style={{
            marginBottom: "1rem",
            padding: "1rem",
            background: passengerCount?.error ? "#fef2f2" : "#f0f9ff",
            border: passengerCount?.error ? "1px solid #ef4444" : "1px solid #3b82f6",
            borderRadius: "8px",
          }}>
            {passengerCountLoading ? (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span>Calculating passengers...</span>
              </div>
            ) : passengerCount?.error ? (
              <div style={{ color: "#b91c1c", fontSize: "0.875rem" }}>
                <strong>⚠ Error:</strong> {passengerCount.error}
              </div>
            ) : passengerCount ? (
              <div style={{ color: "#1e40af", fontSize: "0.875rem" }}>
                <strong>Passenger Count: </strong>
                There are total <strong style={{ fontSize: "1.1em" }}>{passengerCount.count}</strong> passenger{passengerCount.count !== 1 ? "s" : ""} scheduled for
                <strong> {tripForm.tripType}</strong> between
                <strong> {passengerCount.normalizedMinTime}</strong> and
                <strong> {passengerCount.normalizedMaxTime}</strong> on selected date.
                <br />
                <strong>{passengerCount.doneCount || 0}</strong> passenger{passengerCount.doneCount !== 1 ? "s " : " "}
                are already part of a <strong>{tripForm.tripType}</strong> trip on selected date.
              </div>
            ) : (
              <div style={{ fontSize: "0.875rem", color: "#c90000ff" }}>
                Enter all fields correctly to see passenger count.
              </div>
            )}
          </div>
        )}

        {eligiblePassengers.length > 0 && (
          <>
            <label>Select Vehicles</label>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "1rem",
              margin: "1rem 0rem",
            }}>
              {cars.length === 0 ? (
                <div style={{
                  padding: "2rem",
                  textAlign: "center",
                  background: "#fef2f2",
                  border: "1px solid #ef4444",
                  borderRadius: "8px",
                  color: "#b91c1c",
                }}>
                  <h3 style={{ margin: 0, marginBottom: "0.5rem" }}>
                    No Vehicles Available
                  </h3>
                  <p style={{ margin: 0, fontSize: "0.875rem" }}>
                    You need to add vehicles to the system before creating trips.
                    Please go to the Vehicles tab to add vehicles.
                  </p>
                </div>
              ) : (
                cars.map((c, carIdx) => {
                  const isSelected = selectedCars.some(sc => sc.carId === c._id);
                  return (
                    <div
                      key={c._id}
                      onClick={() => toggleCarSelection(c._id)}
                      style={{
                        border: isSelected ? `3px solid ${carColors[carIdx % carColors.length]}` : "1px solid #a9a9a9ff",
                        borderRadius: "8px",
                        padding: "1rem",
                        cursor: "pointer",
                        background: isSelected ? "#f0fdf4" : "#fff",
                        transition: "all 0.2s",
                      }}
                    >
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}>
                        <div style={{ fontWeight: "bold", marginBottom: "0.25rem" }}>
                          {c.name}
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          style={{ width: "min-content", accentColor: "blue" }}
                        />
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        {c.number}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        {c.driver?.name || "No driver"} • Capacity: {c.capacity}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {selectedCars.length > 0 && (
              <div style={{ marginTop: "2rem" }}>
                <h4>Vehicle Assignments</h4>
                {selectedCars.map((selectedCar) => {
                  const car = selectedCar.car;
                  return (
                    <div key={selectedCar.carId} style={{
                      marginBottom: "2rem",
                      padding: "1rem",
                      border: `2px solid ${carColors[cars.findIndex(c => c._id === selectedCar.carId) % carColors.length]}`,
                      borderRadius: "8px",
                      background: "#f9fafb",
                    }}>
                      <h5 style={{ margin: "0 0 1rem 0" }}>
                        {car.name} ({car.number}) - Capacity: {car.capacity}
                      </h5>
                      
                      <div style={{ marginBottom: "1rem" }}>
                        <label style={{ fontWeight: "bold", marginBottom: "0.25rem" }}>
                          Note for this vehicle (optional)
                        </label>
                        <textarea
                          value={selectedCar.note}
                          onChange={(e) => updateCarNote(selectedCar.carId, e.target.value)}
                          placeholder={`Note for ${car.name}...`}
                          rows={2}
                          style={{
                            width: "100%",
                            padding: "0.5rem",
                            border: "1px solid #d1d5db",
                            borderRadius: "4px",
                            fontSize: "0.875rem",
                            resize: "vertical",
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: "1rem" }}>
                        <div style={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center",
                          marginBottom: "0.5rem"
                        }}>
                          <strong>Assigned Passengers ({selectedCar.passengers.length}/{car.capacity})</strong>
                          {selectedCar.passengers.length > 0 && (
                            <button
                              type="button"
                              className="btn danger"
                              style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                              onClick={() => {
                                if (window.confirm("Remove all passengers from this vehicle?")) {
                                  bulkRemovePassengers(selectedCar.carId, selectedCar.passengers.map(p => p._id));
                                }
                              }}
                            >
                              Remove All
                            </button>
                          )}
                        </div>
                        
                        {selectedCar.passengers.length === 0 ? (
                          <p style={{ fontSize: "0.875rem", color: "#6b7280", margin: "0.5rem 0" }}>
                            No passengers assigned yet
                          </p>
                        ) : (
                          <>
                            {bulkSelectedPassengers[selectedCar.carId]?.length > 0 && (
                              <div style={{ marginBottom: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                                <span style={{ fontWeight: 600, color: "#2d7a64", fontSize: "0.875rem" }}>
                                  {bulkSelectedPassengers[selectedCar.carId].length} selected
                                </span>
                                <button
                                  type="button"
                                  className="btn danger"
                                  style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem" }}
                                  onClick={() => {
                                    bulkRemovePassengers(selectedCar.carId, bulkSelectedPassengers[selectedCar.carId]);
                                  }}
                                >
                                  Remove Selected
                                </button>
                                {selectedCars.length > 1 && (
                                  <>
                                    <select
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          bulkMovePassengers(selectedCar.carId, e.target.value, bulkSelectedPassengers[selectedCar.carId]);
                                          e.target.value = "";
                                        }
                                      }}
                                      style={{ fontSize: "0.75rem", padding: "0.3rem", width: "min-content" }}
                                      className="tms-select"
                                    >
                                      <option value="">Move to...</option>
                                      {selectedCars
                                        .filter(sc => sc.carId !== selectedCar.carId)
                                        .map(sc => (
                                          <option key={sc.carId} value={sc.carId}>
                                            {sc.car.name} {sc.car.number}
                                          </option>
                                        ))}
                                    </select>
                                  </>
                                )}
                                <button
                                  type="button"
                                  className="btn secondary"
                                  style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem" }}
                                  onClick={() => {
                                    setBulkSelectedPassengers(prev => {
                                      const newState = { ...prev };
                                      delete newState[selectedCar.carId];
                                      return newState;
                                    });
                                  }}
                                >
                                  Clear Selection
                                </button>
                              </div>
                            )}
                            <div className="scroll-x">
                              <table className="table table-list" style={{ gap: '1.25rem'}}>
                                <thead>
                                  <tr>
                                    <th style={{ width: "50px" }}>
                                      <input 
                                        type="checkbox"
                                        checked={
                                          selectedCar.passengers.length > 0 &&
                                          selectedCar.passengers.every(p => 
                                            bulkSelectedPassengers[selectedCar.carId]?.includes(p._id)
                                          )
                                        }
                                        onChange={() => toggleSelectAllInCar(
                                          selectedCar.carId,
                                          selectedCar.passengers.map(p => p._id)
                                        )}
                                        style={{ cursor: "pointer" }}
                                      />
                                    </th>
                                    <th>Sr#</th>
                                    <th>Name</th>
                                    <th>Stop</th>
                                    <th>Coordinator</th>
                                    {!bulkSelectedPassengers[selectedCar.carId]?.length && <th>Actions</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedCar.passengers
                                    .slice()
                                    .sort((a, b) => {
                                      const stopA = stops.find(s => s._id === a.stop?._id || s._id === a.stop);
                                      const stopB = stops.find(s => s._id === b.stop?._id || s._id === b.stop);
                                      const orderA = stopA ? stopA.order : 999;
                                      const orderB = stopB ? stopB.order : 999;
                                      return tripForm.tripType === "morning" ? orderA - orderB : orderB - orderA;
                                    })
                                    .map((p, index) => {
                                    const isSelected = bulkSelectedPassengers[selectedCar.carId]?.includes(p._id);
                                    return (
                                      <tr key={p._id}>
                                        <td>
                                          <input 
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => togglePassengerSelection(selectedCar.carId, p._id)}
                                            style={{ cursor: "pointer" }}
                                          />
                                        </td>
                                        <td>{index + 1}</td>
                                        <td style={{
                                            backgroundColor: GENDER_BG_COLORS[p.gender] || "transparent"
                                        }}>{p.name}</td>
                                        <td>{p.stop?.name || "-"}</td>
                                        <td>
                                          <input
                                            type="checkbox"
                                            checked={selectedCar.coordinators.includes(p._id)}
                                            onChange={() => toggleCoordinator(selectedCar.carId, p._id)}
                                            style={{ accentColor: "#2d7a64" }}
                                          />
                                        </td>
                                        {!bulkSelectedPassengers[selectedCar.carId]?.length && (
                                          <td>
                                            <div style={{ display: "flex", gap: "0.25rem" }}>
                                              <button
                                                type="button"
                                                className="btn danger"
                                                style={{ fontSize: "0.7rem", padding: "0.2rem 0.4rem" }}
                                                onClick={() => removePassengerFromCar(selectedCar.carId, p._id)}
                                              >
                                                Remove
                                              </button>
                                              {selectedCars.length > 1 && (
                                                <select
                                                  onChange={(e) => {
                                                    if (e.target.value) {
                                                      movePassenger(selectedCar.carId, e.target.value, p._id);
                                                      e.target.value = "";
                                                    }
                                                  }}
                                                  style={{ fontSize: "0.9rem", padding: "0.2rem" }}
                                                  className="tms-select"
                                                >
                                                  <option value="">Move to...</option>
                                                  {selectedCars
                                                    .filter(sc => sc.carId !== selectedCar.carId)
                                                    .map(sc => (
                                                      <option key={sc.carId} value={sc.carId}>
                                                        {sc.car.name} {sc.car.number}
                                                      </option>
                                                    ))}
                                                </select>
                                              )}
                                            </div>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>

                      {unassignedPassengers.length > 0 && (
                        <div>
                          <strong style={{ fontSize: "0.875rem" }}>Add from unassigned:</strong>
                          <div style={{ 
                            marginTop: "0.5rem", 
                            display: "flex", 
                            flexWrap: "wrap", 
                            gap: "0.5rem" 
                          }}>
                            {unassignedPassengers.map(p => (
                              <button
                                key={p._id}
                                type="button"
                                className="btn"
                                style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                                onClick={() => addPassengerToCar(selectedCar.carId, p)}
                              >
                                + {p.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <br />
        <button
          className="btn success"
          type="submit"
          style={{ marginTop: 16 }}
          disabled={isSubmitting || selectedCars.length === 0}
        >
          {isSubmitting ? "Saving..." : "Save as Draft"}
        </button>
      </form>
    </div>
  );
};

export default TripCreate;
